import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { DeclareIssueDialog } from './DeclareIssueDialog'
import { IssueSheet } from './IssueSheet'
import { formatIssueReference } from '../../lib/issue-reference'
import { useTicketSubtypes, subtypeLabel, TICKET_DOMAINS } from '../../hooks/useTicketSubtypes'
import { useCan } from '../../contexts/PermissionsContext'
import { EmptyState } from '../ui/empty-state'
import { mdiAccount, mdiCalendar, mdiClipboardAlertOutline, mdiMessageOutline, mdiPlus } from '@mdi/js'

type TargetType = 'asset' | 'user' | 'contract' | 'document'

type Props = {
    targetType: TargetType
    targetEntityUid: string
    showCreateButton?: boolean
    showClosedToggle?: boolean
    renderIssueSheet?: boolean
}

const PRIORITY_COLORS: Record<number, string> = {
    1: 'bg-red-500',
    2: 'bg-orange-500',
    3: 'bg-yellow-500',
    4: 'bg-blue-500',
    5: 'bg-gray-400',
}

function formatDate(d: string) {
    return new Date(d).toLocaleDateString()
}

function isOverdue(d: string) {
    return new Date(d) < new Date()
}

export function EntityIssuesList({
    targetType,
    targetEntityUid,
    showCreateButton = true,
    showClosedToggle = true,
    renderIssueSheet = true,
}: Props) {
    const { org } = useParams({ strict: false })
    const orgCode = org || 'demo'
    const navigate = useNavigate()
    const search = useSearch({ strict: false }) as { issue?: string; domain?: string }
    const { t, i18n } = useTranslation('issues')
    const { byCode } = useTicketSubtypes(orgCode)
    // Reporting an incident is `issues.write`; the list itself stays readable.
    const canWriteIssues = useCan('issues.write')
    const [dialogOpen, setDialogOpen] = useState(false)
    const [showClosed, setShowClosed] = useState(false)
    const [includeAnnotations, setIncludeAnnotations] = useState(false)

    const domainFilter = search.domain
    const setDomainFilter = (domain: string | undefined) => {
        navigate({
            search: (prev: Record<string, unknown>) => ({ ...prev, domain }) as never,
            replace: true,
        })
    }

    const openIssueInSheet = (uid: string) => {
        navigate({
            search: (prev: Record<string, unknown>) => ({ ...prev, issue: uid }) as never,
            replace: false,
        })
    }
    const closeIssueSheet = () => {
        navigate({
            search: (prev: Record<string, unknown>) => {
                const next = { ...prev }
                delete (next as { issue?: string }).issue
                return next as never
            },
            replace: false,
        })
    }

    const statusFilter = showClosed ? 'open,in_progress,closed' : 'open,in_progress'

    const { data, isLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        {
            params: {
                path: { orgCode },
                query: {
                    target_entity_type: targetType,
                    target_entity_uid: targetEntityUid,
                    status: statusFilter,
                    assignee_uid: 'all',
                    limit: 50,
                    type: includeAnnotations ? '*' : undefined,
                } as unknown as Record<string, unknown>,
            },
        },
    )

    const allIssues = data?.data ?? []
    const issues = domainFilter
        ? allIssues.filter((i) => (i as { domain?: string }).domain === domainFilter)
        : allIssues
    // Domains actually present on this entity's tickets, to keep the filter honest.
    const presentDomains = TICKET_DOMAINS.filter((d) =>
        allIssues.some((i) => (i as { domain?: string }).domain === d),
    )

    return (
        <div data-testid="entity-issues-list" className="space-y-3">
            {/* Action button — free-standing (no box), top-right */}
            {showCreateButton && canWriteIssues && (
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => setDialogOpen(true)}
                        aria-label={t('create.declare', { defaultValue: 'Report an incident' })}
                        title={t('create.declare', { defaultValue: 'Report an incident' })}
                        data-testid="entity-issues-create-btn"
                    >
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('create.declare', { defaultValue: 'Report an incident' })}</span>
                    </Button>
                </div>
            )}

            {/* Filters box */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
                {showClosedToggle && (
                    <Select
                        value={showClosed ? 'all' : 'open'}
                        onValueChange={(v) => setShowClosed(v === 'all')}
                    >
                        <SelectTrigger className="w-auto h-9 gap-1" data-testid="entity-issues-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="open" data-testid="entity-issues-status-open">{t('list.filter_open', { defaultValue: 'Open' })}</SelectItem>
                            <SelectItem value="all" data-testid="entity-issues-status-all">{t('list.filter_closed_incl', { defaultValue: 'With closed' })}</SelectItem>
                        </SelectContent>
                    </Select>
                )}
                <Select
                    value={includeAnnotations ? 'all' : 'exclude'}
                    onValueChange={(v) => setIncludeAnnotations(v === 'all')}
                >
                    <SelectTrigger className="w-auto h-9 gap-1" data-testid="entity-issues-annotations-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="exclude" data-testid="entity-issues-annotations-exclude">{t('list.annotations_exclude', { defaultValue: 'Hide annotations' })}</SelectItem>
                        <SelectItem value="all" data-testid="entity-issues-annotations-all">{t('list.annotations_all', { defaultValue: 'Show all' })}</SelectItem>
                    </SelectContent>
                </Select>
                {presentDomains.length > 0 && (
                    <Select
                        value={domainFilter ?? 'all'}
                        onValueChange={(v) => setDomainFilter(v === 'all' ? undefined : v)}
                    >
                        <SelectTrigger className="w-auto h-9 gap-1" data-testid="entity-issues-domain-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="entity-issues-domain-all">{t('ticket.domain_all')}</SelectItem>
                            {presentDomains.map((d) => (
                                <SelectItem key={d} value={d} data-testid={`entity-issues-domain-${d}`}>
                                    {t(`ticket.domain_${d}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('list.loading', { defaultValue: 'Loading…' })}</p>
            ) : issues.length === 0 ? (
                <EmptyState
                    variant="inline"
                    className="py-12"
                    icon={mdiClipboardAlertOutline}
                    title={t('list.empty', { defaultValue: 'No issues yet.' })}
                    data-testid="entity-issues-empty"
                />
            ) : (
                <div className="divide-y divide-border rounded-md border border-border">
                    {issues.map((issue) => {
                        const color = PRIORITY_COLORS[issue.priority] || PRIORITY_COLORS[3]
                        return (
                            <Link
                                key={issue.uid}
                                to="/$org/issues/$uid"
                                params={{ org: orgCode, uid: issue.uid }}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
                                data-testid={`entity-issue-row-${issue.uid}`}
                                onClick={(e) => {
                                    if (
                                        e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
                                        e.button !== 0
                                    ) return
                                    e.preventDefault()
                                    openIssueInSheet(issue.uid)
                                }}
                            >
                                <span
                                    className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${color}`}
                                    title={t(`priority.${issue.priority}`, { defaultValue: '' })}
                                />
                                <span className="text-sm font-medium text-foreground flex-1 truncate">
                                    {/* The reference first, as in the queue, the
                                        detail header and the conversation chips:
                                        a ticket is named the same way wherever
                                        it appears (spec 2026-09-13-01). */}
                                    {(() => {
                                        const reference = formatIssueReference(
                                            (issue as { number?: number }).number, i18n.language)
                                        return reference ? (
                                            <span
                                                className="text-muted-foreground font-normal tabular-nums mr-1.5"
                                                data-testid={`entity-issue-number-${issue.uid}`}
                                            >
                                                {reference}
                                            </span>
                                        ) : null
                                    })()}
                                    {issue.title}
                                </span>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {(() => {
                                        const sub = (issue as { subtype?: string | null }).subtype
                                        const label = sub ? subtypeLabel(byCode[sub], i18n.language) : ''
                                        return label ? (
                                            <Badge variant="secondary" className="hidden sm:inline-flex text-xs" data-testid={`entity-issue-subtype-${issue.uid}`}>
                                                {label}
                                            </Badge>
                                        ) : null
                                    })()}
                                    {(issue.nb_comments ?? 0) > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <AppIcon path={mdiMessageOutline} size="xs" />
                                            {issue.nb_comments}
                                        </span>
                                    )}
                                    {issue.due_date && (
                                        <span
                                            className={`flex items-center gap-1 text-xs ${
                                                isOverdue(issue.due_date)
                                                    ? 'text-destructive font-medium'
                                                    : 'text-muted-foreground'
                                            }`}
                                        >
                                            <AppIcon path={mdiCalendar} size="xs" />
                                            {formatDate(issue.due_date)}
                                        </span>
                                    )}
                                    {issue.assigned_to && (
                                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <AppIcon path={mdiAccount} size="xs" />
                                            {issue.assigned_to.name}
                                        </span>
                                    )}
                                    <Badge variant="outline" className="text-xs">
                                        {t(`status.${issue.status}`, { defaultValue: issue.status })}
                                    </Badge>
                                </div>
                            </Link>
                        )
                    })}
                </div>
            )}

            {/* The same declaration surface the Photo tab opens — one set of
                questions across the product, whichever button was pressed. */}
            <DeclareIssueDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                kind="incident"
                targetType={targetType}
                targetEntityUid={targetEntityUid}
                assetUid={targetType === 'asset' ? targetEntityUid : undefined}
                declaredFrom="issues_tab"
            />
            {renderIssueSheet && (
                <IssueSheet issueUid={search.issue ?? null} onClose={closeIssueSheet} />
            )}
        </div>
    )
}
